import { motion } from 'framer-motion';
import { IndianRupee, Calendar, Hash, TrendingUp, PiggyBank } from 'lucide-react';
import { LoanSchedule, formatCurrency, formatDate } from '@/lib/loanCalculator';
import { cn } from '@/lib/utils';

interface EMIResultsProps {
  data: LoanSchedule | null;
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  subValue?: string;
  highlight?: boolean;
  delay?: number;
}

function StatCard({ icon: Icon, label, value, subValue, highlight, delay = 0 }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3 }}
      className={cn(
        "relative overflow-hidden rounded-2xl p-4 sm:p-5 transition-all duration-300",
        highlight
          ? "bg-gradient-primary text-primary-foreground shadow-lg shadow-primary/20"
          : "bg-card border border-border hover:shadow-md"
      )}
    >
      {highlight && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-foreground/10 to-transparent" />
      )}
      <div className="relative">
        <div className={cn(
          "inline-flex p-2 rounded-lg mb-3",
          highlight ? "bg-primary-foreground/20" : "bg-primary/10"
        )}>
          <Icon className={cn("w-4 h-4", highlight ? "text-primary-foreground" : "text-primary")} />
        </div>
        <p className={cn(
          "text-xs font-medium mb-1",
          highlight ? "text-primary-foreground/80" : "text-muted-foreground"
        )}>
          {label}
        </p>
        <p className={cn(
          "text-xl sm:text-2xl font-bold tracking-tight truncate",
          highlight ? "text-primary-foreground" : "text-foreground"
        )}>
          {value}
        </p>
        {subValue && (
          <p className={cn(
            "text-xs mt-1",
            highlight ? "text-primary-foreground/70" : "text-muted-foreground"
          )}>
            {subValue}
          </p>
        )}
      </div>
    </motion.div>
  );
}

export function EMIResults({ data }: EMIResultsProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64 bg-card rounded-2xl border border-border">
        <p className="text-muted-foreground">Enter loan details to see results</p>
      </div>
    );
  }

  const { result } = data;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* EMI - Primary Highlight */}
      <StatCard
        icon={IndianRupee}
        label="Monthly EMI"
        value={formatCurrency(result.emi)}
        subValue="Payable every month"
        highlight
        delay={0}
      />

      {/* Other Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          icon={TrendingUp}
          label="Total Interest"
          value={formatCurrency(result.totalInterest)}
          delay={0.1}
        />
        <StatCard
          icon={PiggyBank}
          label="Total Payable"
          value={formatCurrency(result.totalPayable)}
          delay={0.15}
        />
        <StatCard
          icon={Hash}
          label="No. of EMIs"
          value={result.numberOfEMIs.toString()}
          subValue={`${Math.floor(result.numberOfEMIs / 12)}y ${result.numberOfEMIs % 12}m`}
          delay={0.2}
        />
        <StatCard
          icon={Calendar}
          label="Loan End Date"
          value={formatDate(result.endDate)}
          delay={0.25}
        />
      </div>
    </motion.div>
  );
}
